import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

type TableRowActionsLayout = 'inline' | 'wrap' | 'stack';

interface TableRowActionsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** inline: single row (default). wrap: allow wrapping for many actions. stack: vertical (e.g. input + buttons). */
  layout?: TableRowActionsLayout;
}

const layoutClass: Record<TableRowActionsLayout, string> = {
  inline: 'table-row-actions table-row-actions--inline',
  wrap: 'table-row-actions table-row-actions--wrap',
  stack: 'table-row-actions table-row-actions--stack',
};

/** Compact action button group for DataTable row columns. */
export function TableRowActions({
  children,
  layout = 'inline',
  className,
  ...props
}: TableRowActionsProps) {
  return (
    <div className={cn(layoutClass[layout], className)} {...props}>
      {children}
    </div>
  );
}

/** Compact outlined action button for DataTable rows. */
export function TableRowActionLink({
  className,
  children,
  title,
  tooltip,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tooltip?: ReactNode }) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      tooltip={tooltip ?? title}
      className={cn(
        'shrink-0 border border-slate-300 bg-white font-medium text-slate-800 shadow-sm',
        'hover:border-slate-400 hover:bg-slate-50',
        'dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
}

export function isActionsColumn(col: { key: string; header: string; actions?: boolean }): boolean {
  if (col.actions) return true;
  if (col.key === 'actions') return true;
  return col.header.trim().toLowerCase() === 'actions';
}

export const ACTIONS_COLUMN_CLASS =
  'table-actions-cell min-w-[18rem] w-[1%] whitespace-nowrap align-middle';
